import type { PhotoModalImage } from "@/components/ui/photo-modal";
import type { GalleryDress } from "@/types/gallery";

export interface GalleryHeroImage {
  url: string | null;
  alt: string;
}

/** Display name for a gallery dress, with one fallback used on every surface. */
export function getGalleryDressName(
  dress: Pick<GalleryDress, "title" | "prompt">
): string {
  return dress.title?.trim() || dress.prompt?.trim() || "Custom dress";
}

/**
 * The image a gallery dress is shown with in listings: a lifestyle photo
 * picked deterministically from the dress id (so the same dress always shows
 * the same photo, while the catalog varies), falling back to the front render.
 */
export function getGalleryHeroImage(dress: GalleryDress): GalleryHeroImage {
  const sortedLifestyle = (dress.lifestyle_images ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order);

  let heroLifestyle: (typeof sortedLifestyle)[number] | null = null;
  if (sortedLifestyle.length > 0) {
    let hash = 0;
    for (let i = 0; i < dress.id.length; i += 1) {
      hash = (hash * 31 + dress.id.charCodeAt(i)) | 0;
    }
    heroLifestyle = sortedLifestyle[Math.abs(hash) % sortedLifestyle.length];
  }

  return {
    url: heroLifestyle?.image_url || dress.image_url || null,
    alt: heroLifestyle
      ? `${getGalleryDressName(dress)} worn by a model`
      : dress.prompt || "Dress design",
  };
}

function addImage(
  images: PhotoModalImage[],
  seen: Set<string>,
  image: PhotoModalImage | null
) {
  if (!image?.src || image.src.trim().length === 0 || seen.has(image.src)) {
    return;
  }

  seen.add(image.src);
  images.push(image);
}

export function getGalleryPhotoImages(
  dress: GalleryDress,
  heroImage?: GalleryHeroImage
): PhotoModalImage[] {
  const images: PhotoModalImage[] = [];
  const seen = new Set<string>();
  const fallbackName = getGalleryDressName(dress);

  if (heroImage?.url) {
    addImage(images, seen, {
      id: `hero-${heroImage.url}`,
      src: heroImage.url,
      alt: heroImage.alt,
    });
  }

  addImage(images, seen, {
    id: "front",
    src: dress.image_url || "",
    alt: `${fallbackName} - front view`,
  });

  addImage(images, seen, {
    id: "back",
    src: dress.back_image_url || "",
    alt: `${fallbackName} - back view`,
  });

  (dress.lifestyle_images ?? [])
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .forEach((image, index) => {
      addImage(images, seen, {
        id: image.id || `lifestyle-${index}`,
        src: image.image_url,
        alt: image.label
          ? `${fallbackName} - ${image.label.replace(/[_-]+/g, " ")}`
          : `${fallbackName} - lifestyle image ${index + 1}`,
      });
    });

  return images;
}
